/**
 * Display Devices Routes Configuration
 */

import { MODULE_HEADER_BG } from '@/theme/moduleHeader';
import { lazy } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { reportsRoutes } from '@/modules/core/reports/routes';
import { ModuleBannerProvider, type ModuleBanner } from '@/modules/core/layout/moduleBanner';
import GlobalUdaHeaderSelect from '@/components/GlobalUdaHeaderSelect';

// Setup and Reports draw the slim module masthead in the Display Devices blue. Setup
// carries the working-UDA picker (DD has no UDA scope of its own); the report screens
// draw the picker the report data obeys themselves.
const DD_BANNER_BG = MODULE_HEADER_BG;
const DD_SETUP_BANNER: ModuleBanner = { background: DD_BANNER_BG, title: 'Setup', udaSlot: <GlobalUdaHeaderSelect /> };
const DD_REPORTS_BANNER: ModuleBanner = { background: DD_BANNER_BG, title: 'Reports' };

const PassThroughLayout = () => <Outlet />;

// Setup section layout: renders a sticky "Back to Setup" bar on every subpage
// (e.g. /setup/tariff, /setup/displayer-type) but not on the setup index itself.
const SetupLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isIndex = /\/setup\/?$/.test(location.pathname);
  return (
    <div className="flex flex-col h-full min-h-0">
      {!isIndex && (
        <div className="sticky top-0 z-30 flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <button
            type="button"
            // Go to the Setup index explicitly. Relative '..' navigation
            // resolved past /setup to the module root (dashboard) under this
            // nested route, so truncate the current path at /setup instead.
            onClick={() => navigate(location.pathname.replace(/\/setup(\/.*)?$/, '/setup'))}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-purple-700 dark:text-gray-300 dark:hover:text-purple-400 transition-colors"
          >
            <i className="ri-arrow-left-line text-base" />
            Back to Setup
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0">
        <Outlet />
      </div>
    </div>
  );
};

const DDIntegratedDashboard = lazy(() => import('../pages/DDIntegratedDashboard'));

// Legacy Displayers & Devices — historical offline records, reference only.
// Kept in its own section so nothing here sits next to a live workflow action.
const LegacyLayout = lazy(() => import('../legacy/LegacyLayout'));
const LegacyDashboard = lazy(() => import('../legacy/LegacyDashboard'));
const LegacyDisplayersList = lazy(() => import('../legacy/LegacyDisplayersList'));
const LegacyDevicesList = lazy(() => import('../legacy/LegacyDevicesList'));
const LegacyUpload = lazy(() => import('../legacy/LegacyUpload'));

// Masters
const DisplayerType = lazy(() => import('../masters/displayer-type'));
const EntityType = lazy(() => import('../masters/entity-type'));
const DeviceType = lazy(() => import('../masters/device-type'));
const DeviceSize = lazy(() => import('../masters/device-size'));
const DeviceCategory = lazy(() => import('../masters/device-category'));
const SiteType = lazy(() => import('../masters/site-type'));
const FeeType = lazy(() => import('../masters/fee-type'));
const ViolationType = lazy(() => import('../masters/violation-type'));
const DocumentType = lazy(() => import('../masters/document-type'));
const NoticeType = lazy(() => import('../masters/notice-type'));
const Tariff = lazy(() => import('../masters/tariff'));
const SetupPage = lazy(() => import('@/modules/core/setup'));

// Transactions — individual forms (still needed for /new and /:id/edit routes)
const DisplayerList = lazy(() => import('../transactions/displayer/DisplayerList'));
const DisplayerForm = lazy(() => import('../transactions/displayer/DisplayerForm'));
const DeviceList = lazy(() => import('../transactions/device/DeviceList'));
const DeviceForm = lazy(() => import('../transactions/device/DeviceForm'));
const DemandForm = lazy(() => import('../transactions/demand/DemandForm'));
const NoticeForm = lazy(() => import('../transactions/notice/NoticeForm'));
const InspectionForm = lazy(() => import('../transactions/inspection/InspectionForm'));

// Merged tab pages
const DemandCollectionPage = lazy(() => import('../pages/DemandCollectionPage'));
const DemandCreationPage = lazy(() => import('../pages/DemandCreationPage'));
const CompliancePage = lazy(() => import('../pages/CompliancePage'));
const PenaltiesPage = lazy(() => import('../pages/PenaltiesPage'));
const LabourCessList = lazy(() => import('../transactions/labour-cess/LabourCessList'));
const ActionsPage = lazy(() => import('../pages/ActionsPage'));
const WorkflowPage = lazy(() => import('../pages/WorkflowPage'));
const InboxPage = lazy(() => import('../pages/InboxPage'));
const SlaBreachReport = lazy(() => import('../pages/SlaBreachReport'));
const GlobalSearchPage = lazy(() => import('../pages/GlobalSearch'));
const ChangeHistoryAudit = lazy(() => import('../pages/ChangeHistoryAudit'));

// Masters - Structural
const EngineerEmpanel = lazy(() => import('../masters/engineer-empanel'));
const UniversityMaster = lazy(() => import('../masters/university'));

// Masters - New GO compliance
const ProhibitedPlace = lazy(() => import('../masters/prohibited-place'));
// NOTE: the Content Regulation (GO §18) and Exemptions (GO Rule 11) master
// screens were removed — neither was wired to anything. Content checks were
// never read by the inspection form (it carries its own hard-coded checklist),
// and no application, fee or workflow ever consulted the exemption master.
// Finance Head Mapping was removed too. The GL code per demand reason still
// lives on ds_dmnd_rsn_lst_t.gl_cd and is still printed on receipts — it just is
// not editable from Setup any more. The screen also read `chartofaccounts`,
// which is a per-ULB tenant table, through this module's fixed `microservice`
// schema, so it only ever worked on dev (where a stray copy of that table
// exists) and errored on UAT and production.

// Rates — permission rates are State-wide only (GO Ms.253 Annexure-VIII) and are
// maintained from Setup → Tariff → Permission Fee. The old per-ULB override
// screen (`/setup/rates`) has been removed: ULBs have no authority to vary the
// statutory schedule.

// Reports — uses shared core/reports module (data-driven from rpt_* tables)

export const displayDevicesRoutes: RouteObject[] = [
  {
    index: true,
    element: <DDIntegratedDashboard />
  },
  // ── Global Search ──
  {
    path: 'search',
    element: <GlobalSearchPage />
  },
  // ── Displayers (standalone) ──
  {
    path: 'displayers',
    element: <DisplayerList />
  },
  {
    path: 'displayers/:id/edit',
    element: <DisplayerForm />
  },
  // ── Devices (standalone) ──
  {
    path: 'devices',
    element: <DeviceList />
  },
  {
    path: 'devices/new',
    element: <DeviceForm />
  },
  {
    path: 'devices/:id/edit',
    element: <DeviceForm />
  },
  // ── Demand & Collection (merged tab page) ──
  {
    path: 'demand-collection',
    element: <DemandCollectionPage />
  },
  // ── Create Demands (standalone — bulk demand creation flow) ──
  {
    path: 'create-demands',
    element: <DemandCreationPage />
  },
  // Keep form routes for creating/editing demands
  {
    path: 'demands/new',
    element: <DemandForm />
  },
  {
    path: 'demands/:id/edit',
    element: <DemandForm />
  },
  // ── Compliance (merged tab page: Notices + Inspections + Structural) ──
  // Uses wildcard so each tab gets its own URL: /compliance/notices, /compliance/inspections, etc.
  {
    path: 'compliance/*',
    element: <CompliancePage />
  },
  // ── Penalties / Fines (standalone — sidebar entry) ──
  {
    path: 'penalties',
    element: <PenaltiesPage />
  },
  // ── Labour Cess register (GO Ms.No.253 Rule 22 / Rule 29(c) remittance) ──
  {
    path: 'labour-cess',
    element: <LabourCessList />
  },
  // Keep form routes for creating/editing notices & inspections
  {
    path: 'notices/new',
    element: <NoticeForm />
  },
  {
    path: 'notices/:id/edit',
    element: <NoticeForm />
  },
  {
    path: 'inspections/new',
    element: <InspectionForm />
  },
  {
    path: 'inspections/:id/edit',
    element: <InspectionForm />
  },
  // ── Actions (merged tab page: Renewals + Withdrawals + Auctions) ──
  //   Regularization is no longer here — it flows through the Applications /
  //   inbox workflow as a device-permission flagged rglrztn_in.
  {
    path: 'actions',
    element: <ActionsPage />
  },
  // ── Workflow (merged tab page: Inbox + Service Requests) ──
  {
    path: 'workflow',
    element: <WorkflowPage />
  },
  // ── Digital Signature — RETIRED as a standalone screen. Signing now happens
  //    inline under the Inbox's Approve button, with the pending-signature
  //    worklists living on the Inbox's Commissioner-only "Signature" tab. These
  //    routes stay so old bookmarks / links land somewhere useful instead of 404.
  { path: 'digital-signature', element: <Navigate to="../inbox" replace /> },
  { path: 'pending-digisign', element: <Navigate to="../inbox" replace /> },
  { path: 'device-digital-signature', element: <Navigate to="../inbox" replace /> },
  { path: 'pending-device-digisign', element: <Navigate to="../inbox" replace /> },
  // ── Applications (workflow requests). `applications` is the canonical path;
  //    `service-requests` kept as an alias so old links/bookmarks still work. ──
  { path: 'applications', element: <WorkflowPage /> },
  { path: 'service-requests', element: <WorkflowPage /> },
  { path: 'inbox', element: <InboxPage /> },
  { path: 'sla-breaches', element: <SlaBreachReport /> },
  // ── Change History (audit trail) — cross-entity search over the before/after
  //    log. Per-record trails live in the Displayer profile and the list rows;
  //    this is the "who ever touched this field" view.
  { path: 'audit/change-history', element: <ChangeHistoryAudit /> },
  { path: 'change-history', element: <Navigate to="../audit/change-history" replace /> },
  { path: 'notices', element: <CompliancePage /> },
  { path: 'inspections', element: <CompliancePage /> },
  { path: 'removals', element: <CompliancePage /> },
  // Structural Certificates is hidden from the module (see CompliancePage).
  // The path stays as a redirect rather than being deleted: the sidebar is
  // DB-driven (mnu_lst_t), so a menu row pointing here can outlive the screen,
  // and landing on Notices beats landing on a 404.
  { path: 'structural-certificates', element: <Navigate to="../compliance/notices" replace /> },
  { path: 'dcb', element: <DemandCollectionPage /> },
  { path: 'demands', element: <DemandCreationPage /> },
  { path: 'renewals', element: <ActionsPage /> },
  { path: 'withdrawals', element: <ActionsPage /> },
  { path: 'appeals', element: <ActionsPage /> },
  { path: 'auctions', element: <ActionsPage /> },
  // Deep link straight to the refund queue — the tab id matches, so ActionsPage
  // opens on it. Distinct from 'security-deposits' above, which is the DCB view
  // of deposits coming IN.
  { path: 'deposit-refunds', element: <ActionsPage /> },
  { path: 'enforcement', element: <CompliancePage /> },
  { path: 'penalty-fines', element: <PenaltiesPage /> },
  { path: 'penalties', element: <PenaltiesPage /> },
  { path: 'security-deposits', element: <DemandCollectionPage /> },
  { path: 'insurance', element: <CompliancePage /> },
  { path: 'receipts', element: <DemandCollectionPage /> },
  // ── Legacy Displayers & Devices (historical, offline records) ──
  {
    path: 'legacy',
    element: <LegacyLayout />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard',  element: <LegacyDashboard /> },
      { path: 'displayers', element: <LegacyDisplayersList /> },
      { path: 'devices',    element: <LegacyDevicesList /> },
      { path: 'uploads',    element: <LegacyUpload /> },
    ],
  },
  // ── Reports ──
  { path: 'reports', element: <ModuleBannerProvider value={DD_REPORTS_BANNER}><PassThroughLayout /></ModuleBannerProvider>, children: reportsRoutes },
  // ── Setup (masters) — uses framework core/setup for tile grid ──
  {
    path: 'setup',
    element: <SetupLayout />,
    children: [
      { index: true, element: <ModuleBannerProvider value={DD_SETUP_BANNER}><SetupPage /></ModuleBannerProvider> },
      { path: 'displayer-type',  element: <DisplayerType /> },
      { path: 'entity-type',     element: <EntityType /> },
      { path: 'device-type',     element: <DeviceType /> },
      { path: 'device-size',     element: <DeviceSize /> },
      { path: 'device-category', element: <DeviceCategory /> },
      { path: 'site-type',       element: <SiteType /> },
      { path: 'fee-type',        element: <FeeType /> },
      { path: 'violation-type',  element: <ViolationType /> },
      { path: 'document-type',   element: <DocumentType /> },
      { path: 'notice-type',     element: <NoticeType /> },
      { path: 'tariff',          element: <Tariff /> },
      { path: 'engineer-empanel', element: <EngineerEmpanel /> },
      { path: 'university',      element: <UniversityMaster /> },
      { path: 'prohibited-place', element: <ProhibitedPlace /> },
    ],
  },
];

export default displayDevicesRoutes;
